<template>
  <div class="container mx-auto mt-10 vld-parent">
    <loading :active.sync="$apollo.loading" :is-full-page="true" />
    <t-modal variant="clean" v-model="editModal" header="Edit User">
      <div class="mx-4 my-3">
        <div class="flex flex-col w-64 lg:w-96">
          <label for="chat_id" class="flex flex-col w-full mb-4">
            <span>Chat ID</span>
            <t-input id="chat_id" type="text" v-model="editUser.chat_id" />
          </label>
          <label for="subscription" class="flex flex-col w-full mb-4">
            <span>Subscription</span>
            <t-textarea
              id="subscription"
              class="h-32 resize-none"
              v-model="editUser.subscriptions"
            />
          </label>
        </div>
      </div>
      <template #footer>
        <t-button variant="success" @click="handleConfirmEditUsers">
          Save Changes
        </t-button>
      </template>
    </t-modal>
    <div class="px-10 py-10 mx-20 mb-10 border border-gray-200 rounded-lg shadow-lg">
      <h2 class="mb-6 text-2xl font-light">Users</h2>
      <t-table
        :headers="[
          { value: 'email', text: 'E-mail' },
          { value: 'role', text: 'Role' },
          { value: 'chat_id', text: 'Chat ID' },
          { value: 'subscriptions', text: 'Subscriptions' },
          { value: 'actions', text: 'Actions' },
        ]"
        :data="users"
      >
        <template v-slot:row="props">
          <tr :class="[props.trClass, props.rowIndex % 2 === 1 ? 'bg-gray-100' : '']">
            <td :class="props.tdClass">{{ props.row.email }}</td>
            <td :class="props.tdClass" class="capitalize">{{ props.row.role }}</td>
            <td :class="props.tdClass">{{ props.row.chat_id }}</td>
            <td :class="props.tdClass">
              <p>
                Click edit to see subscriptions
              </p>
            </td>
            <td :class="props.tdClass">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                class="h-8 text-white text-green-400"
                @click="handleEdit(props.row._id)"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </td>
          </tr>
        </template>
      </t-table>
    </div>
    <div class="px-10 py-10 mx-20 border border-gray-200 rounded-lg shadow-lg vld-parent">
      <loading :active.sync="storeListLoading" :is-full-page="false" />
      <h2 class="mb-6 text-2xl font-light">Store Lists</h2>
      <div v-for="(storeList, index) in storeLists" :key="index" class="mb-4">
        <div class="flex justify-between mb-4">
          <h3 class="text-xl capitalize">{{ storeList.name }}</h3>
          <t-button variant="success" @click="handleSaveStoreListEdit(index)">
            Save
          </t-button>
        </div>
        <t-textarea v-model="storeList.list" class="w-full resize-none" />
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import GET_USERS from '../graphql/GetUsers';
import GET_STORELISTS from '../graphql/GetStoreLists';
import EDIT_STORELISTS from '../graphql/EditStoreList';

export default {
  name: 'Admin',
  data() {
    return {
      editModal: false,
      editUser: {},
      storeLists: [],
      storeListLoading: false,
    };
  },
  async created() {
    const { data } = await this.$apollo.query({ query: GET_STORELISTS });
    if (data.StoreListMany) {
      this.storeLists = data.StoreListMany.map((e) => ({ ...e, list: e.list.join(', ') }));
    }
  },
  apollo: {
    users: { query: GET_USERS, update: (data) => data.UserMany },
  },
  methods: {
    async handleSaveStoreListEdit(index) {
      try {
        this.storeListLoading = true;
        const { list, _id } = this.storeLists[index];
        const newList = list.split(',').map((e) => e.trim());
        await this.$apollo.mutate({
          mutation: EDIT_STORELISTS,
          variables: { record: { _id, list: newList } },
        });
        this.$toasted.success('Edited store list!', {
          theme: 'bubble',
          position: 'top-center',
          duration: 3000,
        });
      } catch (e) {
        console.log(e);
        this.$toasted.error('Error while editing store list', {
          theme: 'bubble',
          position: 'top-center',
          duration: 3000,
        });
      } finally {
        this.storeListLoading = false;
      }
    },
    handleEdit(id) {
      // eslint-disable-next-line
      const editingUser = this.users.find((e) => e._id === id);
      this.editUser = {
        ...editingUser,
        subscriptions: editingUser.subscriptions.join('\n').replaceAll(':', '\t'),
      };
      this.editModal = true;
    },
    async handleConfirmEditUsers() {
      // eslint-disable-next-line
      const { _id, chat_id, subscriptions } = this.editUser;
      const parsedSubscriptions = subscriptions.split('\n').map((e) => e.replaceAll('\t', ':'));
      await this.$apollo.mutate({
        mutation: gql`
          mutation UserUpdateById($record: UpdateByIdUserInput!) {
            UserUpdateById(record: $record) {
              record {
                _id
                chat_id
                subscriptions
              }
            }
          }
        `,
        variables: {
          // eslint-disable-next-line object-curly-newline
          record: { _id, chat_id, subscriptions: parsedSubscriptions },
        },
        update: (store) => {
          const data = store.readQuery({ query: GET_USERS });
          const index = data.UserMany.findIndex(
            (e) => e._id === _id, // eslint-disable-line no-underscore-dangle
          );
          // eslint-disable-next-line
          data.UserMany[index].chat_id = chat_id;
          data.UserMany[index].subscriptions = parsedSubscriptions;
          store.writeQuery({ query: GET_USERS, data });
          this.editModal = false;
          this.editUser = {};
        },
      });
    },
  },
};
</script>

<style></style>
